import { getCountry } from "pages/common/utils";

import { ValidationSchema, Validator } from "@bps/validator";

import { Country } from "./booking";

export interface Address {
  street1: string;
  city: string;
  postCode: string;
  suburb?: string;
  addressState?: string;
  country: string;
}

export const createAddressValidationSchema = (
  isRequired: boolean
): ValidationSchema<Address> => {
  const validator = new Validator<Address>();

  return {
    street1: [
      validator.custom().func<"street1">(value => {
        if (!value && isRequired) return "Please enter a valid street address";
        if (value && value.length > 40)
          return "The street address must be 40 characters or less";
        if (value && !/^[\w/\-,'.\s]+$/.test(value))
          return "The street address contains invalid characters";
        return value;
      })
    ],

    city: validator.custom().func<"city">((value, parent) => {
      const isNZ = parent.country === Country.NewZealand;

      if (isNZ) {
        if (!value && isRequired) return "Please enter a valid city";
        if (value && value.length > 40)
          return "The suburb name must be 40 characters or less";
        if (value && !/^[a-z'\-\s]+$/i.test(value))
          return "The suburb name contains invalid characters";
      }

      return value;
    }),

    suburb: validator.custom().func<"suburb">((value, parent) => {
      const isAU = !parent.country || parent.country === Country.Australia;

      if (isAU) {
        if (!value && isRequired) return "Please enter a valid suburb";
        if (value && value.length > 40)
          return "The suburb name must be 40 characters or less";
        if (value && !/^[a-z'\-\s]+$/i.test(value))
          return "The suburb name contains invalid characters";
      }

      return value;
    }),

    addressState: validator.custom().func<"addressState">((value, parent) => {
      const isAU = !parent.country || parent.country === Country.Australia;
      if (isAU && !value && isRequired) {
        return "Please select a valid state";
      }
      return value;
    }),

    postCode: [
      validator.custom().func<"postCode">(value => {
        if (!value && isRequired) return "Please enter a valid postcode";
        if (value && !/^\d{4}$/.test(value))
          return "The postcode must be four digits";
        return value;
      })
    ],

    country: validator.custom().func<"country">(value => {
      if (!value && isRequired) return "Please select a country";
      return value;
    })
  };
};

/**
 * An address match found by google Autocomplete
 */
export interface AddressMatch {
  id: string;
  description: string;
}

export class AddressRestrictions {
  public static MinFullTextSearchLength: number = 3;
  public static MinSearchLengthForManualEntry: number = 15;
}

export const TimeZoneBasedCountry = getCountry() || Country.NewZealand;

export const getDefaultAddress = (country?: string): Address => ({
  street1: "",
  city: "",
  postCode: "",
  suburb: "",
  addressState: "",
  country: country ?? TimeZoneBasedCountry
});
